import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Zap, Users, GitBranch, DollarSign } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <span
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                UserLife
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/app/dashboard">
                <Button variant="outline">Dashboard</Button>
              </Link>
              <Link to="/app/joiner">
                <Button className="bg-primary-600 hover:bg-primary-700">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-primary-50 py-20 sm:py-24 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                Automate Your User Lifecycle
              </div>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-tight"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Onboard and offboard employees in
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-700">
                  {' '}
                  minutes
                </span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                Automate your entire user lifecycle with workflows that integrate seamlessly with
                your HR systems and Microsoft 365.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/app/dashboard" data-testid="cta-dashboard-button">
                  <Button
                    size="lg"
                    className="bg-primary-600 hover:bg-primary-700 shadow-xl shadow-primary-600/30 text-lg px-8 py-6"
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/app/joiner" data-testid="cta-joiner-button">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                    Start Joiner Workflow
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-slate-600">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-slate-600">Setup in minutes</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-primary-400/20 rounded-2xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
                alt="User lifecycle platform dashboard"
                className="rounded-2xl shadow-2xl relative z-10 border-4 border-white"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">95%</div>
              <div className="text-slate-400">Time Saved</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">3 mins</div>
              <div className="text-slate-400">Avg Workflow</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-slate-400">Compliance</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 sm:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Everything you need
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Powerful features to manage your user lifecycle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: GitBranch,
                title: 'Workflow Automation',
                description:
                  'Configure Joiner, Mover, and Leaver workflows with intuitive YAML DSL',
                color: 'from-blue-500 to-blue-600',
              },
              {
                icon: Users,
                title: 'HR Integration',
                description: 'Seamless sync with Employment Hero, BambooHR, Workday and more',
                color: 'from-purple-500 to-purple-600',
              },
              {
                icon: DollarSign,
                title: 'Cost Tracking',
                description: 'Real-time per-user cost calculation with pro-rata licensing',
                color: 'from-green-500 to-green-600',
              },
            ].map((feature, idx: any) => (
              <div
                key={idx}
                className="group bg-white rounded-2xl border border-slate-200 hover:border-primary-300 p-8 hover:shadow-2xl transition-all duration-300"
              >
                <div
                  className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                >
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to streamline your user lifecycle?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join teams who&apos;ve saved thousands of hours with automated workflows
          </p>
          <Link to="/app/dashboard" data-testid="cta-get-started-button">
            <Button
              size="lg"
              className="bg-white text-primary-600 hover:bg-slate-50 shadow-2xl text-lg px-10 py-6"
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>© 2026 UserLife. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
